import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Pagination,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useDocuments, useDeleteDocument } from '../../../hooks/useDocs';
import { IDocumentFilters } from '../../../Types/Docs';
import { DOCUMENT_STATUS_COLORS } from '../constants';
import { APP_PAGES } from '../../../pages';
import { toast } from '../../../Utils/toast';
import {
  formatPipelineLastError,
  summarizeLastSyncOutcome,
} from '../../../Utils/fullSyncSummary';
import {
  triggerFullSyncAsync,
  getFullPipelineSyncStatus,
} from '../../../Utils/docsApi';
import ConfirmationDialog from '../../../Components/ConfirmationDialog';

const Library: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<IDocumentFilters>({
    page: 1,
    limit: 10,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingFullSyncRef = useRef(false);
  const mountedRef = useRef(true);
  const docListHydratedRef = useRef(false);

  const stopFullSyncPolling = () => {
    if (pollTimerRef.current != null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    isPollingFullSyncRef.current = false;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopFullSyncPolling();
    };
  }, []);

  const { documents, total, isLoading, error, refetch } = useDocuments(filters, false); // Disable auto-fetch, we'll handle it manually
  const { deleteDoc, isDeleting } = useDeleteDocument();

  useEffect(() => {
    if (!isLoading) docListHydratedRef.current = true;
  }, [isLoading]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: search || undefined,
        status: statusFilter !== 'all' ? (statusFilter as any) : undefined,
        page: 1, // Reset to first page on filter change
      }));
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [search, statusFilter]);

  useEffect(() => {
    refetch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDocumentToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    const { id, name } = documentToDelete;
    setDeleteDialogOpen(false);
    try {
      await deleteDoc(id);
      toast.success(`Document "${name}" deleted successfully`);
      refetch(filters);
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setDocumentToDelete(null);
    }
  };

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const { downloadDocument } = await import('../../../Utils/docsApi');
      const blob = await downloadDocument(id);
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const doc = documents.find((d) => d.id === id);
      a.download = doc?.displayName || doc?.name || 'document';
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
      
      toast.success('Download started');
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to download document';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const parseSyncConflictPayload = (
    data: unknown
  ): { code?: string; message?: string } | undefined => {
    if (!data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    const detail = d.detail;
    if (typeof detail === 'string') {
      try {
        const parsed = JSON.parse(detail) as { code?: string; message?: string };
        return parsed;
      } catch {
        return { message: detail };
      }
    }
    if (detail && typeof detail === 'object') {
      return detail as { code?: string; message?: string };
    }
    if (typeof d.message === 'string') return { message: d.message };
    if (typeof d.code === 'string') return { code: d.code };
    return undefined;
  };

  const FULL_SYNC_POLL_MS = 2500;
  const FULL_SYNC_POLL_FIRST_MS = 500;

  /**
   * Full pipeline flow: POST /sync/async, then GET /sync/status until running is false.
   * Inspect lastError if set; otherwise use lastSync for counts (and SyncResult.errors if present).
   */
  const handleFullSync = async () => {
    stopFullSyncPolling();
    setIsSyncing(true);
    try {
      const accepted = await triggerFullSyncAsync();
      if (!mountedRef.current) return;
      toast.success(
        `Full sync started at ${accepted.startedAt ? new Date(accepted.startedAt).toLocaleString() : 'now'}…`
      );
      isPollingFullSyncRef.current = true;

      const poll = async (): Promise<void> => {
        if (!isPollingFullSyncRef.current || !mountedRef.current) return;
        try {
          const s = await getFullPipelineSyncStatus();
          if (!isPollingFullSyncRef.current || !mountedRef.current) return;

          if (!s.running) {
            stopFullSyncPolling();
            if (mountedRef.current) setIsSyncing(false);

            if (s.lastError) {
              toast.error(formatPipelineLastError(s.lastError));
            } else {
              const { headline, supplemental } = summarizeLastSyncOutcome(s.lastSync);
              toast.success(`${headline} Refreshing documents…`);
              if (supplemental) toast.info(supplemental);
            }
            if (mountedRef.current) await refetch(filters);
            return;
          }

          pollTimerRef.current = setTimeout(() => void poll(), FULL_SYNC_POLL_MS);
        } catch (pollErr: unknown) {
          stopFullSyncPolling();
          if (mountedRef.current) {
            setIsSyncing(false);
            const ax = pollErr as { response?: { data?: unknown }; message?: string };
            const payload = parseSyncConflictPayload(ax.response?.data);
            const msg =
              payload?.message ||
              ax.message ||
              'Lost connection while checking sync status.';
            toast.error(msg);
          }
        }
      };

      pollTimerRef.current = setTimeout(() => void poll(), FULL_SYNC_POLL_FIRST_MS);
    } catch (err: unknown) {
      if (mountedRef.current) setIsSyncing(false);
      const custom = err as { status?: number; message?: string; responseBody?: unknown };
      const payload = parseSyncConflictPayload(
        typeof custom.responseBody !== 'undefined'
          ? custom.responseBody
          : (err as { response?: { data?: unknown } }).response?.data
      );

      const is409 =
        custom.status === 409 ||
        (err as { response?: { status?: number } }).response?.status === 409;
      const syncRunningCode =
        custom.message === 'sync_already_running' ||
        payload?.code === 'sync_already_running' ||
        payload?.message?.includes('sync_already_running');

      if (is409 && syncRunningCode) {
        toast.error(
          'A full sync is already running. Wait for it to finish, then try again.'
        );
      } else if (is409) {
        toast.error(
          payload?.message ??
            'Sync could not start because another sync is already in progress.'
        );
      } else {
        const ax = err as { response?: { data?: unknown }; message?: string };
        const msg =
          parseSyncConflictPayload(ax.response?.data)?.message ||
          ax.message ||
          'Failed to start full sync.';
        toast.error(msg);
      }
    }
  };

  const totalPages = Math.ceil(total / (filters.limit || 10));
  /**
   * While full sync runs (after initial list loaded), keep showing cached rows —
   * progress only on the Sync button.
   */
  const showDocumentsLoader =
    isLoading && (!docListHydratedRef.current || !isSyncing);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return DOCUMENT_STATUS_COLORS.ready;
      case 'processing':
        return DOCUMENT_STATUS_COLORS.processing;
      case 'failed':
        return DOCUMENT_STATUS_COLORS.failed;
      default:
        return '#666666';
    }
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={4}>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => navigate(APP_PAGES.DOCS)}
              disabled={isSyncing}
              data-testid="library-query-button"
              aria-label="Query Documents"
            >
              Query Documents
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch(filters)}
              disabled={isLoading || isSyncing}
              data-testid="library-refresh-button"
              aria-label="Refresh list"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={
                isSyncing ? (
                  <CircularProgress size={18} color="inherit" aria-hidden />
                ) : (
                  <SyncIcon />
                )
              }
              onClick={handleFullSync}
              disabled={isSyncing}
              data-testid="library-sync-button"
              aria-label={isSyncing ? 'Full sync in progress' : 'Run full sync'}
            >
              {isSyncing ? 'Syncing…' : 'Sync'}
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              data-testid="library-search-input"
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusChange}
                data-testid="library-status-filter"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Table */}
        {error && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#FFEBEE' }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        <TableContainer component={Paper} data-testid="docs-library-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showDocumentsLoader ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={2}>
                      <CircularProgress size={24} />
                      <Typography>Loading documents...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No documents found
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow
                    key={doc.id}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F8F8F8' } }}
                    onClick={() => navigate(`${APP_PAGES.DOCS_DETAIL.replace(':id', doc.id)}`)}
                    data-testid={`docs-document-row-${doc.id}`}
                  >
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {doc.displayName || doc.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={doc.status}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(doc.status),
                          color: '#fff',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{doc.source}</TableCell>
                    <TableCell>
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(doc.id);
                        }}
                        disabled={downloadingId === doc.id}
                        aria-label={`Download ${doc.name}`}
                        data-testid={`docs-document-${doc.id}-download-button`}
                      >
                        {downloadingId === doc.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(doc.id, doc.displayName || doc.name);
                        }}
                        disabled={isDeleting}
                        aria-label={`Delete ${doc.name}`}
                        data-testid={`docs-document-${doc.id}-delete-button`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={filters.page || 1}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Container>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDocumentToDelete(null);
        }}
        confirmColor="error"
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default Library;
